import { create } from 'zustand';
import { api, apiErrorMessage } from '@/lib/api';
import { asArray } from '@/lib/collections';
import { toast } from './ui.store';
import type { PlatformId } from '@ai-media/shared';

export interface DashboardData {
  rangeDays: number;
  generatedAt: string;
  totals: Array<{ key: string; label: string; total: number; previous?: number }>;
  series: Array<{ date: string; engagements: number; impressions: number; reach: number }>;
  statusCounts: Record<string, number>;
  topPosts: Array<{
    id: string;
    title: string | null;
    platformIds: PlatformId[];
    engagements: number;
    publishedAt: string | null;
  }>;
  platformBreakdown: Array<{ platformId: PlatformId; posts: number; engagements: number; impressions: number }>;
  accounts: Array<{ platform_id: PlatformId; account_name: string; status: string; followers: number }>;
}

interface AnalyticsState {
  dashboard: DashboardData | null;
  loading: boolean;
  days: number;
  setDays: (days: number) => void;
  loadDashboard: (days?: number) => Promise<void>;
  exportCsv: () => Promise<void>;
}

/**
 * The dashboard payload is rendered directly, so every collection is normalised
 * on the way in: a missing field must degrade to an empty section, never to a
 * `Cannot read properties of undefined (reading 'map')` in the render.
 */
function normaliseDashboard(data: DashboardData | null | undefined): DashboardData | null {
  if (!data) return null;
  return {
    ...data,
    totals: asArray(data.totals),
    series: asArray(data.series),
    statusCounts: data.statusCounts ?? {},
    topPosts: asArray(data.topPosts).map((post) => ({ ...post, platformIds: asArray(post.platformIds) })),
    platformBreakdown: asArray(data.platformBreakdown),
    accounts: asArray(data.accounts),
  };
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  dashboard: null,
  loading: false,
  days: 7,

  setDays: (days) => {
    set({ days });
    void get().loadDashboard(days);
  },

  loadDashboard: async (days) => {
    const range = days ?? get().days;
    set({ loading: true });
    try {
      const { data } = await api.get<DashboardData>('/analytics/dashboard', {
        params: { days: range },
      });
      set({ dashboard: normaliseDashboard(data) });
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      set({ loading: false });
    }
  },

  exportCsv: async () => {
    try {
      const { data } = await api.get('/analytics/export', {
        params: { days: get().days, format: 'csv' },
        responseType: 'text',
      });
      const blob = new Blob([data as string], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `analytics-${get().days}d.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },
}));

export interface LinkedAccount {
  id: string;
  platform_id: PlatformId;
  account_name: string;
  external_id: string;
  followers: number;
  status: string;
  last_synced_at: string | null;
  configured: boolean;
}

interface AccountsState {
  accounts: LinkedAccount[];
  platforms: Array<{ id: PlatformId; label: string; configured: boolean; requiresReview: boolean }>;
  loading: boolean;
  load: () => Promise<void>;
  connect: (platform: PlatformId) => Promise<void>;
  sync: (id: string) => Promise<void>;
  disconnect: (id: string) => Promise<void>;
}

export const useAccountsStore = create<AccountsState>((set, get) => ({
  accounts: [],
  platforms: [],
  loading: false,

  load: async () => {
    set({ loading: true });
    try {
      const [accountsRes, platformsRes] = await Promise.all([
        api.get<{ data: LinkedAccount[] }>('/accounts'),
        // /social/platforms returns {id,label,configured,requiresReview,scopes}.
        // /accounts/platforms only returns bare platform ids — it cannot drive the UI.
        api.get<{ data: AccountsState['platforms'] }>('/social/platforms'),
      ]);
      set({
        accounts: asArray(accountsRes.data?.data),
        platforms: asArray(platformsRes.data?.data),
      });
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      set({ loading: false });
    }
  },

  connect: async (platform) => {
    try {
      // The API responds with { url, platform }.
      const { data } = await api.get<{ url: string }>(`/social/connect/${platform}`);
      if (!data?.url) throw new Error('Provider did not return an authorization URL');
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },

  sync: async (id) => {
    try {
      await api.post(`/accounts/${id}/refresh`);
      toast('Account synced', 'success');
      await get().load();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },

  disconnect: async (id) => {
    try {
      await api.delete(`/accounts/${id}`);
      toast('Account disconnected', 'medium');
      await get().load();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },
}));
